import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Chip, Avatar, Stack, IconButton,
  CircularProgress, Tooltip, Badge,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Star as StarIcon,
  DragIndicator as DragIcon,
  Flag as FlagIcon,
  People as PeopleIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import {
  fetchPipeline, updateLeadStatus,
  type LeadThread, type LeadStatus,
  STATUS_CONFIG, AI_SCORE_CONFIG, PRIORITY_CONFIG, PIPELINE_ORDER,
} from "./leadApi";
import { formatEventDate } from "./leadCellUtils";
import LeadThreadDrawer from "./LeadThreadDrawer";
import LeadNumberBadge from "./LeadNumberBadge";
import { useLeadBus } from "../../hooks/useLeadBus";

const NAVY = "#1E4D8F";
const GOLD = "#D4AF37";

export default function LeadPipeline() {
  const navigate = useNavigate();
  const [columns, setColumns] = useState<Record<string, LeadThread[]>>({});
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const dragItem = useRef<{ id: number; fromCol: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPipeline();
      setColumns(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ── Live sync ─ status drops, claims, assignments anywhere in
     the app reshuffle the kanban columns instantly. */
  useLeadBus(() => { loadData(); });

  const handleDragStart = (id: number, fromCol: string) => {
    dragItem.current = { id, fromCol };
  };

  const handleDrop = async (toCol: string) => {
    if (!dragItem.current || dragItem.current.fromCol === toCol) return;
    const { id, fromCol } = dragItem.current;
    dragItem.current = null;

    // Optimistic update
    setColumns((prev) => {
      const from = [...(prev[fromCol] || [])];
      const to = [...(prev[toCol] || [])];
      const idx = from.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const [item] = from.splice(idx, 1);
      item.status = toCol as LeadStatus;
      to.unshift(item);
      return { ...prev, [fromCol]: from, [toCol]: to };
    });

    try {
      await updateLeadStatus(id, toCol as LeadStatus);
    } catch {
      loadData(); // revert on error
    }
  };

  return (
    <Box sx={{ p: 3, height: "calc(100vh - 100px)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
            Lead Pipeline
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Drag & drop leads between stages
          </Typography>
        </Box>
        <IconButton onClick={loadData}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: GOLD }} />
        </Box>
      ) : (
        /* Kanban Board */
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: 1.5,
            overflow: "auto",
            pb: 2,
          }}
        >
          {PIPELINE_ORDER.map((status) => {
            const cfg = STATUS_CONFIG[status];
            const items = columns[status] || [];
            return (
              <Box
                key={status}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(status)}
                sx={{
                  minWidth: 260,
                  maxWidth: 300,
                  flex: "0 0 260px",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#f8fafc",
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}
              >
                {/* Column Header */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: `3px solid ${cfg.color}`,
                    bgcolor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: NAVY, fontSize: "0.8rem" }}
                  >
                    {cfg.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={items.length}
                    sx={{
                      bgcolor: cfg.color,
                      color: "#fff",
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  />
                </Box>

                {/* Cards */}
                <Box sx={{ flex: 1, overflow: "auto", p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                  {items.map((lead) => (
                    <Paper
                      key={lead.id}
                      draggable
                      onDragStart={() => handleDragStart(lead.id, status)}
                      onClick={() => {
                        if (!Number.isInteger(lead.id) || lead.id <= 0) {
                          // eslint-disable-next-line no-console
                          console.error("Invalid lead ID passed to drawer (pipeline)", lead);
                          return;
                        }
                        // eslint-disable-next-line no-console
                        console.log("Opening lead drawer (pipeline)", lead.id);
                        setSelectedId(lead.id);
                        setDrawerOpen(true);
                      }}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: "1px solid #e5e7eb",
                        borderRadius: 1.5,
                        cursor: "grab",
                        bgcolor: "#fff",
                        "&:hover": {
                          borderColor: GOLD,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        },
                        transition: "all 0.15s",
                        borderLeft: `3px solid ${PRIORITY_CONFIG[lead.priority]?.color || "#6b7280"}`,
                      }}
                    >
                      {/* Name + VIP */}
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Avatar
                          sx={{
                            width: 26,
                            height: 26,
                            bgcolor: GOLD,
                            color: NAVY,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                          }}
                        >
                          {lead.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Typography
                          variant="body2"
                          onClick={(e) => {
                            // Inquiry name always opens the lead's own detail
                            // page — never the customer's master profile. This
                            // preserves the inquiry's identity even when the
                            // customer account is named differently (e.g. two
                            // family members sharing a phone).
                            e.stopPropagation();
                            navigate(`/leads/${lead.id}`);
                          }}
                          title="Open inquiry detail"
                          sx={{
                            fontWeight: 600,
                            color: NAVY,
                            fontSize: "0.8rem",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textTransform: "capitalize",
                            cursor: "pointer",
                            "&:hover": { color: "#6366f1", textDecoration: "underline" },
                          }}
                        >
                          {lead.name?.toLowerCase()}
                        </Typography>
                        {lead.is_vip && (
                          <StarIcon sx={{ color: GOLD, fontSize: 16 }} />
                        )}
                      </Stack>

                      {/* Public lead number — compact slate badge. */}
                      {lead.lead_number && (
                        <Box sx={{ mt: 0.5 }}>
                          <LeadNumberBadge value={lead.lead_number} variant="muted" />
                        </Box>
                      )}

                      {/* Event info */}
                      <Stack spacing={0.3} sx={{ mt: 1 }}>
                        {lead.event_type && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <EventIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                            <Typography variant="caption" sx={{ color: "#64748b", textTransform: "capitalize" }}>
                              {lead.event_type.replace(/_/g, " ")}
                            </Typography>
                          </Stack>
                        )}
                        {lead.guest_count && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PeopleIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                              {lead.guest_count} guests
                            </Typography>
                          </Stack>
                        )}
                        {lead.event_date && (
                          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                            {formatEventDate(lead.event_date)}
                          </Typography>
                        )}
                      </Stack>

                      {/* Bottom badges */}
                      <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                        {lead.ai_score && (
                          <Chip
                            size="small"
                            label={AI_SCORE_CONFIG[lead.ai_score].icon}
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              bgcolor: `${AI_SCORE_CONFIG[lead.ai_score].color}15`,
                            }}
                          />
                        )}
                        {lead.follow_up_required && (
                          <Chip
                            size="small"
                            label="Follow-up"
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              bgcolor: "#fef3c7",
                              color: "#92400e",
                            }}
                          />
                        )}
                      </Stack>
                    </Paper>
                  ))}

                  {items.length === 0 && (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: "#94a3b8",
                        fontSize: "0.8rem",
                        border: "2px dashed #e5e7eb",
                        borderRadius: 1.5,
                      }}
                    >
                      Drop here
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <LeadThreadDrawer
        open={drawerOpen}
        threadId={selectedId}
        onClose={() => setDrawerOpen(false)}
        onUpdated={loadData}
      />
    </Box>
  );
}
